<template>
  <div class="roles-main">
    <div class="detail-section transcript-section">
      <div class="section-title">Transcript</div>
      <div class="chat-scroll">
        <div
          v-for="(message, index) in messageHistory"
          :key="`message-${index}`"
          class="chat-row"
          :class="{'chat-sent': message.sent === 1}"
        >
          <div
            class="chat-avatar"
            :class="message.sent === 1 ? 'avatar-cs' : 'avatar-guest'"
          >
            <i :class="message.sent === 1 ? 'el-icon-service' : 'el-icon-user'" />
          </div>
          <div class="chat-body">
            <div class="chat-meta">
              <span class="chat-name">{{ sayerDisplayName(message) }}</span>
              <span
                class="chat-time"
                v-formatter="{
                  data: message.ts,
                  formatter: 'HH:mm:ss',
                  type: 'date',
                }"
              />
            </div>
            <div
              class="chat-bubble"
              :class="[
                message.sent === 1 ? 'bubble-cs' : 'bubble-guest',
                message.type === 'image' ? 'bubble-image' : '',
              ]"
            >
              <el-image
                v-if="message.type === 'image'"
                hide-on-click-modal
                class="chat-image"
                :src="message.msg"
                :preview-src-list="[message.msg]"
                fit="cover"
              />
              <div v-else v-safe-html="message.msg" class="chat-text" />
            </div>
          </div>
        </div>
        <div class="chat-end">
          <span class="chat-end-line" />
          <span class="chat-end-text">
            {{ chatEndSReason }} · ended at
            <span
              v-formatter="{
                data: form.finishTime,
                formatter: 'HH:mm A',
                type: 'date',
              }"
            />
          </span>
          <span class="chat-end-line" />
        </div>
      </div>
    </div>

    <div class="detail-section info-section">
      <div class="section-title">Info</div>
      <div class="info-scroll">
        <el-descriptions :column="2" size="small" border>
          <el-descriptions-item label="Guest Name">
            {{ form.guestName }}
          </el-descriptions-item>
          <el-descriptions-item label="Agent Name">
            {{ form.agentNickNameList.join() }}
          </el-descriptions-item>
          <el-descriptions-item label="IP">
            {{ form.guestIp }}
          </el-descriptions-item>
          <el-descriptions-item label="Wrap Up">
            {{ form.wrapUpChain }}
          </el-descriptions-item>
          <el-descriptions-item label="Browser">
            {{ form.browser }}
          </el-descriptions-item>
          <el-descriptions-item label="Device">
            {{ form.device }}
          </el-descriptions-item>
          <el-descriptions-item label="From">
            {{ form.fromPage }}
          </el-descriptions-item>
          <el-descriptions-item label="Country">
            {{ form.country }}
          </el-descriptions-item>
          <el-descriptions-item label="Province">
            {{ form.province }}
          </el-descriptions-item>
          <el-descriptions-item label="Status">
            {{ form.status }}
          </el-descriptions-item>
          <el-descriptions-item label="Description">
            {{ form.description }}
          </el-descriptions-item>
          <el-descriptions-item label="Finish Type">
            {{ getFinishReason(form.finishType) }}
          </el-descriptions-item>
          <el-descriptions-item label="Create Time">
            <span
              v-formatter="{
                data: form.createTime,
                formatter: 'YYYY/MM/DD HH:mm:ss',
                type: 'date',
              }"
            />
          </el-descriptions-item>
          <el-descriptions-item label="Finish Time">
            <span
              v-formatter="{
                data: form.finishTime,
                formatter: 'YYYY/MM/DD HH:mm:ss',
                type: 'date',
              }"
            />
          </el-descriptions-item>
          <el-descriptions-item label="Match Time">
            <span
              v-formatter="{
                data: form.matchTime,
                formatter: 'YYYY/MM/DD HH:mm:ss',
                type: 'date',
              }"
            />
          </el-descriptions-item>
          <el-descriptions-item label="Close Time">
            <span
              v-formatter="{
                data: form.closeTime,
                formatter: 'YYYY/MM/DD HH:mm:ss',
                type: 'date',
              }"
            />
          </el-descriptions-item>
          <el-descriptions-item label="Guest Id">
            {{ form.roomName }}
          </el-descriptions-item>
          <el-descriptions-item label="Guest Rating">
            <star-rating
              v-model:rating="form.guestRating"
              :read-only="true"
              star-size="14"
              inline="true"
              :show-rating="false"
            />
          </el-descriptions-item>
          <el-descriptions-item label="Guest Comment" :span="2">
            <div class="info-comment">{{ form.guestComment }}</div>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onMounted, reactive, computed } from '@vue/runtime-core'
import { getSiteRoomId, getFinishReason } from '../../../../api/room'
import { fetchWrapUp } from '../../../../api/wrap-up'
import { isEmpty, startsWith, map } from 'lodash'
import { defineProps } from 'vue'
import StarRating from 'vue-star-rating'

const props = defineProps({
  roomId: {
    type: Number,
    default: () => 0,
  },
})

const form = reactive({
  id: null,
  siteId: null,
  roomName: null,
  guestName: null,
  wrapUpChain: null,
  browser: null,
  device: null,
  fromPage: null,
  country: null,
  province: null,
  status: null,
  description: null,
  guestIp: null,
  createTime: null,
  matchTime: null,
  finishTime: null,
  closeTime: null,
  finishType: null,
  guestRating: null,
  guestComment: null,
  agentNickNameList: [],
  message: [],
})

const wrapUpList = []

async function loadForm(id) {
  const { data: ret } = await getSiteRoomId(id)
  nextTick(() => {
    for (const key in ret) {
      if (Object.keys(form).find(k => k === key)) {
        form[key] = ret[key]
      }
    }

    form.wrapUpChain = processWrapUpList(form.wrapUpChain)
  })
}

// 纯辅助: 取显示名(去角色前缀), 角色由气泡左右+头像表达
function sayerDisplayName(message) {
  return message.senderName ?? 'sys'
}

function processWrapUpList(list) {
  let wrapUpStr = ''
  if (list !== null) {
    const resultList = []
    list.forEach(chain => {
      const chainStr = processWrapUpChain(chain)
      if (chainStr !== '') {
        resultList.push(chainStr)
      }
    })
    wrapUpStr = resultList.join(',')
  }
  return wrapUpStr;
}

function processWrapUpChain(chain) {
  let wrapUpStr = ''
  if (chain !== null) {
    const wrapUpArr = chain.split(',')
    const resultList = []
    wrapUpArr.forEach(element => {
      const existWrapUpList = wrapUpList.value
      const res = existWrapUpList.find(x => x.id === Number(element))
      if (res != null) {
        resultList.push(res.name)
      }
    })

    wrapUpStr = resultList.join('->')
  }
  return wrapUpStr
}

async function loadWrapUp() {
  const { data: ret } = await fetchWrapUp()
  wrapUpList.value = ret
}

onMounted(async () => {
  await loadWrapUp()
  loadForm(props.roomId)
})

const messageHistory = computed(() => {
  if (isEmpty(form.message)) {
    return []
  }

  return map(form.message, item => {
    item = item.body
    item.sent = 1

    if (startsWith(item.senderId, 'gt_')) {
      item.sent = 0
    }

    if (item.type === 'options') {
      item.type = 'text'
      item.msg = '[options]'
    }

    if (item.type === 'option_selected') {
      item.type = 'text'
      if (item.meta.items && item.meta.items.length) {
        item.msg = item.meta.items[0]?.name
      }
    }

    if (item.type === 'file') {
      item.msg = ''

      if (item.files && item.files[0]) {
        const file = item.files[0]

        if (
          ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(file.extension)
        ) {
          item.type = 'image'
          item.msg = file.url
        } else {
          item.msg = `<a href="${file.url}" target="_blank">File</a>`
        }
      }
    }

    return item
  })
})

const chatEndSReason = computed(() => {
  return getFinishReason(form.finishType);
})
</script>

<style scoped>
.roles-main {
  display: flex;
  flex-direction: column;
  /* el-drawer 全高(100vh) 减去 body 上下 padding(20px*2), 取确定高度让内部分区可独立滚动 */
  height: calc(100vh - 40px);
  box-sizing: border-box;
  padding: 4px 8px;
  gap: 14px;
}

/* ---------- 上下分区 ---------- */
.detail-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.transcript-section {
  flex: 3 1 0;
}
.info-section {
  flex: 2 1 0;
}
.section-title {
  flex: 0 0 auto;
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

/* ---------- Transcript 聊天区 ---------- */
.chat-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 6px;
  font-size: 14px;
}
.chat-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
}
.chat-sent {
  flex-direction: row-reverse;
}
.chat-avatar {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
}
.avatar-cs {
  background: #409eff;
}
.avatar-guest {
  background: #c0c4cc;
}
.chat-body {
  max-width: 72%;
  display: flex;
  flex-direction: column;
}
.chat-sent .chat-body {
  align-items: flex-end;
}
.chat-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  line-height: 1;
}
.chat-name {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
}
.chat-time {
  font-size: 11px;
  color: #b0b3b8;
}
.chat-bubble {
  position: relative;
  padding: 8px 12px;
  border-radius: 10px;
  word-break: break-word;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.bubble-guest {
  background: #f4f4f5;
  color: #303133;
  border-top-left-radius: 2px;
}
.bubble-cs {
  background: #ecf5ff;
  color: #1f3a5f;
  border-top-right-radius: 2px;
}
.bubble-image {
  padding: 4px;
  background: transparent;
  box-shadow: none;
}
.chat-text :deep(a) {
  color: #409eff;
  text-decoration: underline;
}
.chat-image {
  width: 140px;
  height: 140px;
  border-radius: 8px;
  display: block;
  cursor: zoom-in;
}
.chat-end {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 4px;
}
.chat-end-line {
  flex: 1;
  height: 1px;
  background: #ebeef5;
}
.chat-end-text {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

/* ---------- Info 区 ---------- */
.info-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  font-size: 14px;
}
.info-scroll :deep(.el-descriptions__label) {
  width: 110px;
  font-weight: 600;
  color: #606266;
  background: #fafafa;
}
.info-comment {
  word-break: break-word;
  white-space: pre-wrap;
}
.info-scroll :deep(.vue-star-rating) {
  vertical-align: middle;
}
</style>
